/**
 * `/portal/e/:token` — the screen a driver lands on after scanning the QR or
 * tapping the link the dispatcher sent.
 *
 * It exchanges the one-time token for a session, stores it under the PORTAL
 * keys, and then explains — once — that this phone is now signed in for good.
 * The "add to home screen" card is the whole point of the screen: a bookmarked
 * browser tab is what makes drivers ask for a new link every week.
 *
 * iPHONE: THE ICON SPENDS THE LINK, NOT SAFARI
 * On iOS a Home Screen icon has its own storage, separate from Safari's, so a
 * session made in Safari does not come along when the driver adds the icon,
 * and the single-use link is already spent. So on iOS (installContext.js):
 *   ios-safari         nothing is exchanged. The driver adds the icon first;
 *                      this screen links no manifest, so the icon opens at
 *                      this very link (why: usePortalChrome's useManifestLink)
 *   ios-other-browser  nothing is exchanged. The driver is asked to open the
 *                      link in Safari
 *   ios-standalone     the icon itself: exchange at once, as below. Later
 *                      launches open the same (spent) link and go straight on
 *                      to /portal while the icon's session lasts
 * Either of the first two can still choose "Inkább a böngészőben folytatom",
 * which runs the ordinary exchange right where they are.
 * Everything else (Android, desktop: `other`) is exactly the flow below.
 */
import React, {
  useCallback, useEffect, useState,
} from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { enrolPortal, hasPortalSession } from '../../Services/PortalApi';
import { firstName, principalName } from './portalFormat';
import PortalShell from './Components/PortalShell';
import PortalGate from './Components/PortalGate';
import BrandLogo from './Components/BrandLogo';
import { AddToHomeFirst, OpenInSafari } from './Components/IosInstallScreens';
import PushSetting from './Components/PushSetting';
import {
  AddToHomeIcon, CheckIcon, LockIcon,
} from './Components/Icons';
import { detectInstallContext, INSTALL_CONTEXTS } from './installContext';

// Where the link must not be spent until the driver has chosen to.
const WAITS_FOR_HOME_SCREEN = new Set([
  INSTALL_CONTEXTS.IOS_SAFARI,
  INSTALL_CONTEXTS.IOS_OTHER_BROWSER,
]);

const SPENT_LINK_BODY = 'A belépési linkek egyszer használhatók fel, és egy idő után lejárnak. Kérj újat az irodától WhatsAppon.';

// The icon always opens at the link it was added from, so once that link is
// spent and the icon's session is gone, only a new icon from a new link helps.
const SPENT_LINK_BODY_IOS_ICON = `${SPENT_LINK_BODY} Utána töröld ezt az ikont a főképernyőről, nyisd meg az új linket Safariban, és tedd ki újra.`;

const isStandalone = () => {
  try {
    return window.matchMedia('(display-mode: standalone)').matches
      || window.navigator.standalone === true;
  } catch {
    return false;
  }
};

const isAndroid = () => /android/i.test(window.navigator.userAgent || '');

// An iPhone that chose to carry on in the browser: an icon added now would
// open signed out (its own storage, and this link is spent), so the usual
// steps would lead the driver straight into the gate. Say what does work.
function IosLaterCard() {
  return (
    <div className="pp-a2hs">
      <div className="pp-a2hs__head">
        <AddToHomeIcon className="pp-a2hs__icon" size={20} />
        <span>Főképernyőre tennéd később?</span>
      </div>
      <div className="pp-a2hs__body">
        iPhone-on a főképernyős ikon külön jelentkezik be, ehhez új link kell.
        Kérj egyet az irodától WhatsAppon, és nyisd meg Safariban.
      </div>
    </div>
  );
}

function AddToHomeScreenCard({ installContext }) {
  const [installPrompt, setInstallPrompt] = useState(null);

  useEffect(() => {
    // Chrome/Android only. Where it fires we can offer a real install button
    // instead of asking the driver to hunt through a browser menu.
    const onBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setInstallPrompt(event);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    return () => window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
  }, []);

  const install = useCallback(async () => {
    if (!installPrompt) return;
    try {
      await installPrompt.prompt();
    } catch {
      // Dismissed or already installed — the manual steps below still apply.
    }
    setInstallPrompt(null);
  }, [installPrompt]);

  // Already installed: the card would be nonsense.
  if (isStandalone()) return null;

  if (WAITS_FOR_HOME_SCREEN.has(installContext)) return <IosLaterCard />;

  const steps = isAndroid()
    ? ['Koppints a böngésző menüjére (⋮)', 'Válaszd az „Alkalmazás telepítése” lehetőséget']
    : ['Koppints a Megosztás ikonra', 'Válaszd a „Hozzáadás a főképernyőhöz” lehetőséget'];

  return (
    <div className="pp-a2hs">
      <div className="pp-a2hs__head">
        <AddToHomeIcon className="pp-a2hs__icon" size={20} />
        <span>Tedd ki a főképernyőre</span>
      </div>
      <div className="pp-a2hs__body">
        Így egy koppintással nyílik, böngésző nélkül — mint egy alkalmazás.
      </div>

      {installPrompt ? (
        <div className="pp-a2hs__steps">
          <button type="button" className="pp-btn pp-btn--primary pp-btn--block" onClick={install}>
            Telepítés
          </button>
        </div>
      ) : (
        <ol className="pp-a2hs__steps">
          {steps.map((step, index) => (
            <li className="pp-a2hs__step" key={step}>
              <span className="pp-a2hs__num">{index + 1}</span>
              <span>{step}</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

export default function PortalEnrolment() {
  const { token } = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  // Fixed for the life of the screen: the phone and browser do not change.
  const [installContext] = useState(detectInstallContext);
  // False while an iPhone browser is showing the "add it first" screen; the
  // link is only spent once this is true.
  const [exchangeHere, setExchangeHere] = useState(
    () => !WAITS_FOR_HOME_SCREEN.has(installContext),
  );
  const [state, setState] = useState('exchanging');
  const [principal, setPrincipal] = useState(null);
  useEffect(() => {
    if (!token || !exchangeHere) return undefined;

    // The iPhone icon opens at this same link on every launch. Once it holds a
    // session there is nothing to exchange: go straight to the day, without a
    // round trip to be told the link is spent (which also works offline).
    if (installContext === INSTALL_CONTEXTS.IOS_STANDALONE && hasPortalSession()) {
      navigate('/portal', { replace: true });
      return undefined;
    }

    // enrolPortal shares one request per token, so every mount simply
    // subscribes and whichever instance is still mounted takes the result.
    // (The old "already started" guard skipped the re-subscribe, so under a
    // remount the only successful reply landed on a cancelled listener and
    // the screen sat on "Belépés folyamatban…" forever.)
    let cancelled = false;
    enrolPortal(token)
      .then((session) => {
        if (cancelled) return;
        setPrincipal(session.principal);
        setState('done');
      })
      .catch(() => {
        if (cancelled) return;
        // Already signed in on this phone — typically the driver tapped the
        // old link again. Nothing is wrong, so take them to their day.
        if (hasPortalSession()) {
          navigate('/portal', { replace: true });
          return;
        }
        setState('failed');
      });

    return () => { cancelled = true; };
  }, [token, navigate, exchangeHere, installContext]);

  const continueHere = useCallback(() => setExchangeHere(true), []);

  if (!exchangeHere) {
    return (
      <PortalShell dark installAtThisUrl>
        {installContext === INSTALL_CONTEXTS.IOS_SAFARI ? (
          <AddToHomeFirst onContinue={continueHere} />
        ) : (
          <OpenInSafari url={`${window.location.origin}${pathname}`} onContinue={continueHere} />
        )}
      </PortalShell>
    );
  }

  if (state === 'exchanging') {
    return (
      <PortalShell dark>
        <div className="pp-topbrand">
          <BrandLogo variant="white" height={48} />
        </div>
        <div className="pp-state" role="status">Belépés folyamatban…</div>
      </PortalShell>
    );
  }

  if (state === 'failed') {
    return (
      <PortalShell dark>
        <PortalGate
          title="Ez a link már nem érvényes"
          body={installContext === INSTALL_CONTEXTS.IOS_STANDALONE
            ? SPENT_LINK_BODY_IOS_ICON
            : SPENT_LINK_BODY}
        />
      </PortalShell>
    );
  }

  const name = firstName(principalName(principal));

  return (
    <PortalShell dark>
      <div className="pp-topbrand">
        <BrandLogo variant="white" height={48} />
      </div>

      <div className="pp-enrol">
        <div className="pp-enrol__mark">
          <CheckIcon size={40} width={3} />
        </div>
        <h1 className="pp-enrol__title">{name ? `Készen vagy, ${name}` : 'Készen vagy'}</h1>
        <p className="pp-enrol__body">
          Ezen a telefonon mostantól be vagy jelentkezve. Nem kell jelszó, és amíg
          használod, nem kell újra bejelentkezned.
        </p>
      </div>

      <AddToHomeScreenCard installContext={installContext} />

      {/*
        The one moment a driver will ever be asked. It sits AFTER the
        add-to-home-screen card on purpose: on an iPhone the permission
        prompt only exists inside the Home Screen app, so someone still in
        Safari is told to install first (PushSetting's
        IOS_NEEDS_HOME_SCREEN branch) rather than shown a button that would
        do nothing. Launched from the icon, the APIs are there and the card
        is a plain "Kérsz értesítést?" with a real button — which is also
        the user gesture the browser requires to raise the prompt at all.

        Missed or refused here, it is never nagged again: the same switch
        lives in the portal's own menu, on every later visit.
      */}
      <PushSetting variant="enrol" />

      <div className="pp-grow" />

      <div className="pp-enrol-foot">
        <button
          type="button"
          className="pp-btn pp-btn--primary pp-btn--block pp-btn--tall"
          onClick={() => navigate('/portal', { replace: true })}
        >
          Tovább a mai naphoz
        </button>
        <div className="pp-privacy">
          <LockIcon size={14} />
          <span>Csak a te fuvarjaidat látod</span>
        </div>
      </div>
    </PortalShell>
  );
}
